import {
  prisma,
  ParticipantGroupType,
  ParticipantType,
  UserRoleAssignmentEntityType,
  type Prisma,
} from "client";
import jwt from "jsonwebtoken";
import { VALIDATION_RULES } from "@sajilni/config";
import { isMongoObjectId } from "@sajilni/utils/ticketQr";
import { getWhiteLabelIdByEventId } from "services/white-label";
import {
  isParticipantOwner,
  pickLinkedOwnerInvitation,
  toPortalTeamMember,
  TEAM_MEMBER_ERROR,
  OWNER_PORTAL_PERMISSIONS,
  type OwnerInvite,
} from "./owner";
import {
  companyAdminLookupEmails,
  pickPreferredCompanyAdmin,
  shouldBlockCompanyAdminEmailChange,
} from "./company-admin.pure";

export {
  isParticipantOwner,
  toPortalTeamMember,
  TEAM_MEMBER_ERROR,
  OWNER_PORTAL_PERMISSIONS,
  ensureCompanyOwnerPortalAccess,
  ensureSponsorOwnerPortalAccess,
  getCompanyTypeFromSuperAdminAssignment,
  resolveCompanyTypeForUser,
  cleanupCompanyTeamUsers,
} from "./owner";

const ownerInviteSelect = {
  where: { isOwner: true as const },
  select: { userId: true, email: true },
  orderBy: { updatedAt: "desc" as const },
};

/** Indexed: Profile @@index([eventId, userId]), Event PK, Exhibitor/Sponsor @@index([eventId, teamIds]). */
export const canAccessBoothAdminPortal = async ({
  userId,
  eventId,
  companyType,
  userEmail,
}: {
  userId: string;
  eventId: string;
  companyType: ParticipantGroupType;
  userEmail?: string | null;
}) => {
  const superAdminRole =
    companyType === ParticipantGroupType.EXHIBITORS
      ? "EXHIBITOR_SUPER_ADMIN"
      : "SPONSOR_SUPER_ADMIN";
  const entityType =
    companyType === ParticipantGroupType.EXHIBITORS
      ? UserRoleAssignmentEntityType.EXHIBITOR
      : UserRoleAssignmentEntityType.SPONSOR;

  const [profile, event, companyFromTeam, superAdminAssignments] =
    await Promise.all([
      prisma.profile.findFirst({
        where: { userId, eventId },
        select: { email: true, exhibitorPermissions: true },
      }),
      prisma.event.findUnique({
        where: { id: eventId },
        select: { networkingSettings: true },
      }),
      companyType === ParticipantGroupType.EXHIBITORS
        ? prisma.exhibitor.findFirst({
            where: { eventId, teamIds: { has: userId } },
            select: {
              id: true,
              email: true,
              exhibitorInvitations: ownerInviteSelect,
            },
          })
        : companyType === ParticipantGroupType.SPONSORS
        ? prisma.sponsor.findFirst({
            where: { eventId, teamIds: { has: userId } },
            select: {
              id: true,
              email: true,
              sponsorInvitations: ownerInviteSelect,
            },
          })
        : Promise.resolve(null),
      prisma.assignmentModel.findMany({
        where: {
          userId,
          entityType,
          role: { name: superAdminRole },
          entityId: { not: null },
        },
        select: { entityId: true },
      }),
    ]);

  const superAdminEntityIds = superAdminAssignments
    .map((a) => a.entityId)
    .filter((id): id is string => Boolean(id));

  let company = companyFromTeam;
  if (!company && superAdminEntityIds.length > 0) {
    company =
      companyType === ParticipantGroupType.EXHIBITORS
        ? await prisma.exhibitor.findFirst({
            where: { eventId, id: { in: superAdminEntityIds } },
            select: {
              id: true,
              email: true,
              exhibitorInvitations: ownerInviteSelect,
            },
          })
        : await prisma.sponsor.findFirst({
            where: { eventId, id: { in: superAdminEntityIds } },
            select: {
              id: true,
              email: true,
              sponsorInvitations: ownerInviteSelect,
            },
          });
  }

  if (!event?.networkingSettings?.switchToBoothAdmin || !company) {
    return false;
  }
  if (profile?.exhibitorPermissions?.adminSettings) return true;
  if (superAdminEntityIds.includes(company.id)) return true;

  const ownerInvites =
    "exhibitorInvitations" in company
      ? company.exhibitorInvitations
      : "sponsorInvitations" in company
      ? company.sponsorInvitations
      : [];
  return isParticipantOwner({
    memberUserId: userId,
    memberEmail: userEmail,
    profileEmail: profile?.email,
    ownerInvite: pickLinkedOwnerInvitation(ownerInvites),
    companyEmail: company.email,
  });
};

/** Company profile / setup screens — profile companySettings or company owner for this event. */
export const canAccessCompanySetupPortal = async ({
  userId,
  eventId,
  companyType,
  userEmail,
}: {
  userId: string;
  eventId: string;
  companyType: ParticipantGroupType;
  userEmail?: string | null;
}) => {
  const superAdminRole =
    companyType === ParticipantGroupType.EXHIBITORS
      ? "EXHIBITOR_SUPER_ADMIN"
      : "SPONSOR_SUPER_ADMIN";
  const entityType =
    companyType === ParticipantGroupType.EXHIBITORS
      ? UserRoleAssignmentEntityType.EXHIBITOR
      : UserRoleAssignmentEntityType.SPONSOR;

  const [profile, companyFromTeam, superAdminAssignments] = await Promise.all([
    prisma.profile.findFirst({
      where: { userId, eventId },
      select: { email: true, exhibitorPermissions: true },
    }),
    companyType === ParticipantGroupType.EXHIBITORS
      ? prisma.exhibitor.findFirst({
          where: { eventId, teamIds: { has: userId } },
          select: {
            id: true,
            email: true,
            exhibitorInvitations: ownerInviteSelect,
          },
        })
      : companyType === ParticipantGroupType.SPONSORS
      ? prisma.sponsor.findFirst({
          where: { eventId, teamIds: { has: userId } },
          select: {
            id: true,
            email: true,
            sponsorInvitations: ownerInviteSelect,
          },
        })
      : Promise.resolve(null),
    prisma.assignmentModel.findMany({
      where: {
        userId,
        entityType,
        role: { name: superAdminRole },
        entityId: { not: null },
      },
      select: { entityId: true },
    }),
  ]);

  const superAdminEntityIds = superAdminAssignments
    .map((a) => a.entityId)
    .filter((id): id is string => Boolean(id));

  let company = companyFromTeam;
  if (!company && superAdminEntityIds.length > 0) {
    company =
      companyType === ParticipantGroupType.EXHIBITORS
        ? await prisma.exhibitor.findFirst({
            where: { eventId, id: { in: superAdminEntityIds } },
            select: {
              id: true,
              email: true,
              exhibitorInvitations: ownerInviteSelect,
            },
          })
        : await prisma.sponsor.findFirst({
            where: { eventId, id: { in: superAdminEntityIds } },
            select: {
              id: true,
              email: true,
              sponsorInvitations: ownerInviteSelect,
            },
          });
  }

  if (!company) return false;
  if (profile?.exhibitorPermissions?.companySettings) return true;
  if (superAdminEntityIds.includes(company.id)) return true;

  const ownerInvites =
    "exhibitorInvitations" in company
      ? company.exhibitorInvitations
      : "sponsorInvitations" in company
      ? company.sponsorInvitations
      : [];
  return isParticipantOwner({
    memberUserId: userId,
    memberEmail: userEmail,
    profileEmail: profile?.email,
    ownerInvite: pickLinkedOwnerInvitation(ownerInvites),
    companyEmail: company.email,
  });
};

const teamMemberSelect = (
  profileEventWhere: { eventId: string } | { event: { slug: string } }
) => ({
  id: true,
  name: true,
  email: true,
  profiles: {
    where: profileEventWhere,
    orderBy: { createdAt: "desc" as const },
    take: 1,
    select: {
      id: true,
      eventId: true,
      name: true,
      email: true,
      jobTitle: true,
      exhibitorPermissions: true,
    },
  },
});

export const getParticipantTeamForPortal = async (
  slug: string,
  userId: string,
  eventId: string,
  type: ParticipantGroupType,
  _lang?: string
) => {
  const profileEventWhere = isMongoObjectId(eventId)
    ? { eventId }
    : { event: { slug } };
  const where = isMongoObjectId(eventId)
    ? { eventId, teamIds: { has: userId } }
    : { event: { slug }, teamIds: { has: userId } };
  const settingsPromise =
    type === ParticipantGroupType.EXHIBITORS
      ? prisma.exhibitorSettings.findUnique({
          where: { eventId },
          select: { supportEmail: true },
        })
      : type === ParticipantGroupType.SPONSORS
      ? prisma.sponsorSettings.findUnique({
          where: { eventId },
          select: { supportEmail: true },
        })
      : Promise.resolve(null);

  if (type === ParticipantGroupType.EXHIBITORS) {
    const [res, settings] = await Promise.all([
      prisma.exhibitor.findFirst({
        where,
        select: {
          id: true,
          email: true,
          team: { select: teamMemberSelect(profileEventWhere) },
          exhibitorInvitations: ownerInviteSelect,
        },
      }),
      settingsPromise,
    ]);
    if (!res) return null;
    return {
      id: res.id,
      email: res.email,
      supportEmail: settings?.supportEmail,
      team: res.team.map((member) =>
        toPortalTeamMember(
          member,
          eventId,
          pickLinkedOwnerInvitation(res.exhibitorInvitations),
          res.email
        )
      ),
    };
  }

  if (type === ParticipantGroupType.SPONSORS) {
    const [res, settings] = await Promise.all([
      prisma.sponsor.findFirst({
        where,
        select: {
          id: true,
          email: true,
          team: { select: teamMemberSelect(profileEventWhere) },
          sponsorInvitations: ownerInviteSelect,
        },
      }),
      settingsPromise,
    ]);
    if (!res) return null;
    return {
      id: res.id,
      email: res.email,
      supportEmail: settings?.supportEmail,
      team: res.team.map((member) =>
        toPortalTeamMember(
          member,
          eventId,
          pickLinkedOwnerInvitation(res.sponsorInvitations),
          res.email
        )
      ),
    };
  }

  return null;
};

const isPrismaUniqueConflict = (error: unknown) =>
  Boolean(
    error &&
      typeof error === "object" &&
      "code" in error &&
      (error as { code?: string }).code === "P2002"
  );

const pickMemberInvitation = ({
  userId,
  previousEmail,
  profileEmail,
  inviteByUser,
  inviteByOldEmail,
  ownerInvite,
  companyEmail,
}: {
  userId: string;
  previousEmail?: string | null;
  profileEmail?: string | null;
  inviteByUser: OwnerInvite | null;
  inviteByOldEmail: OwnerInvite | null;
  ownerInvite: OwnerInvite | null;
  companyEmail?: string | null;
}) => {
  if (
    ownerInvite &&
    isParticipantOwner({
      memberUserId: userId,
      memberEmail: previousEmail,
      profileEmail,
      ownerInvite,
      companyEmail,
    })
  ) {
    return ownerInvite;
  }
  return inviteByUser ?? inviteByOldEmail ?? null;
};

const invitationSelect = { id: true, email: true, userId: true } as const;

export const updatePortalTeamMember = async ({
  userId,
  entityId,
  entityType,
  eventId,
  contactBookGroupId,
  updatedData,
}: {
  userId: string;
  entityId: string;
  entityType: ParticipantType;
  eventId: string;
  contactBookGroupId: string;
  updatedData: {
    name?: string;
    email?: string;
    jobTitle?: string;
    phoneNumber?: string;
    profileId?: string;
    permissions?: Prisma.ExhibitorPermissionsCreateInput;
    eventId?: string;
  };
}) => {
  const nextEmail =
    typeof updatedData.email === "string"
      ? updatedData.email.toLowerCase()
      : undefined;
  if (nextEmail && !VALIDATION_RULES.isEmail.test(nextEmail)) {
    throw new Error(TEAM_MEMBER_ERROR.invalidEmail);
  }

  const existingUser = await prisma.user.findUnique({
    where: { id: userId },
    select: {
      email: true,
      profiles: {
        where: { eventId },
        orderBy: { createdAt: "desc" },
        take: 1,
        select: { id: true, email: true },
      },
    },
  });
  const previousEmail = existingUser?.email?.toLowerCase();
  const profileEmail = existingUser?.profiles[0]?.email;
  const displayedEmail = (profileEmail || previousEmail)?.toLowerCase();
  const emailChanged = Boolean(nextEmail && nextEmail !== displayedEmail);

  const companyPromise =
    entityType === ParticipantType.EXHIBITOR
      ? prisma.exhibitor.findUnique({
          where: { id: entityId },
          select: { email: true },
        })
      : prisma.sponsor.findUnique({
          where: { id: entityId },
          select: { email: true },
        });

  const whiteLabelId =
    emailChanged && nextEmail ? await getWhiteLabelIdByEventId(eventId) : null;
  const [
    takenUser,
    takenInvite,
    inviteByUser,
    inviteByOldEmail,
    ownerInvites,
    company,
  ] = await Promise.all([
    whiteLabelId && nextEmail
      ? prisma.user.findFirst({
          where: {
            email: nextEmail,
            accessibleWhiteLabelId: whiteLabelId,
            NOT: { id: userId },
          },
          select: { id: true },
        })
      : Promise.resolve(null),
    emailChanged && nextEmail
      ? prisma.participantInvitation.findFirst({
          where: { entityId, entityType, email: nextEmail },
          select: invitationSelect,
        })
      : Promise.resolve(null),
    prisma.participantInvitation.findFirst({
      where: { entityId, entityType, userId },
      select: invitationSelect,
    }),
    previousEmail
      ? prisma.participantInvitation.findFirst({
          where: { entityId, entityType, email: previousEmail },
          select: invitationSelect,
        })
      : Promise.resolve(null),
    prisma.participantInvitation.findMany({
      where: { entityId, entityType, isOwner: true },
      select: invitationSelect,
      orderBy: { updatedAt: "desc" },
    }),
    companyPromise,
  ]);

  const ownerInvite = pickLinkedOwnerInvitation(ownerInvites);
  const invite = pickMemberInvitation({
    userId,
    previousEmail,
    profileEmail,
    inviteByUser,
    inviteByOldEmail,
    ownerInvite,
    companyEmail: company?.email,
  });
  const isOwner = isParticipantOwner({
    memberUserId: userId,
    memberEmail: previousEmail,
    profileEmail,
    ownerInvite,
    companyEmail: company?.email,
  });
  const permissions = isOwner
    ? OWNER_PORTAL_PERMISSIONS
    : updatedData.permissions;
  if (emailChanged && nextEmail) {
    if (takenUser || (takenInvite && takenInvite.id !== invite?.id)) {
      throw new Error(TEAM_MEMBER_ERROR.emailAlreadyInUse);
    }
  }

  const profileId =
    updatedData.profileId?.trim() || existingUser?.profiles[0]?.id;
  const profileFields = {
    event: { connect: { id: eventId } },
    jobTitle: updatedData.jobTitle as string,
    name: (updatedData.name as string) ?? "",
    email: nextEmail,
    phoneNumber: updatedData.phoneNumber as string,
    contactBookGroup: { connect: { id: contactBookGroupId } },
    exhibitorPermissions: permissions,
  };
  const shouldSyncInvite = Boolean(
    emailChanged &&
      invite?.id &&
      nextEmail &&
      invite.email &&
      invite.email.toLowerCase() !== nextEmail
  );

  let user;
  try {
    user = await prisma.$transaction(async (tx) => {
      const updated = await tx.user.update({
        where: { id: userId },
        data: {
          name: updatedData.name as string,
          ...(emailChanged && nextEmail ? { email: nextEmail } : {}),
          profiles: profileId
            ? {
                upsert: {
                  where: { id: profileId },
                  create: profileFields,
                  update: profileFields,
                },
              }
            : { create: profileFields },
        },
        select: { id: true },
      });
      if (shouldSyncInvite && invite?.id && nextEmail) {
        await tx.participantInvitation.update({
          where: { id: invite.id },
          data: {
            email: nextEmail,
            token: jwt.sign(
              { entityId, email: nextEmail, entityType },
              process.env.SECRET_HASH_PASSWORD as string
            ),
            userId,
          },
        });
      }
      return updated;
    });
  } catch (error) {
    if (isPrismaUniqueConflict(error)) {
      throw new Error(TEAM_MEMBER_ERROR.emailAlreadyInUse);
    }
    throw error;
  }

  await backfillOwnerInvitationUserId({
    entityId,
    entityType,
    userId,
    memberEmail: previousEmail,
    profileEmail,
    companyEmail: company?.email,
  });

  return user;
};

export const assertNotOwnerTeamMember = async (
  entityId: string,
  entityType: ParticipantType,
  userId: string
) => {
  const company =
    entityType === ParticipantType.EXHIBITOR
      ? await prisma.exhibitor.findUnique({
          where: { id: entityId },
          select: { email: true, eventId: true },
        })
      : entityType === ParticipantType.SPONSOR
      ? await prisma.sponsor.findUnique({
          where: { id: entityId },
          select: { email: true, eventId: true },
        })
      : null;
  const eventId = company?.eventId;
  const [ownerInvites, user] = await Promise.all([
    prisma.participantInvitation.findMany({
      where: { entityId, entityType, isOwner: true },
      select: { userId: true, email: true },
      orderBy: { updatedAt: "desc" },
    }),
    prisma.user.findUnique({
      where: { id: userId },
      select: eventId
        ? {
            email: true,
            profiles: {
              where: { eventId },
              select: { email: true },
              take: 1,
            },
          }
        : { email: true },
    }),
  ]);

  const ownerInvite = pickLinkedOwnerInvitation(ownerInvites);
  const profileEmail =
    user && "profiles" in user ? user.profiles[0]?.email : undefined;
  if (
    [user?.email, profileEmail].some((email) =>
      isParticipantOwner({
        memberUserId: userId,
        memberEmail: email,
        ownerInvite,
        companyEmail: company?.email,
      })
    )
  ) {
    throw new Error("cannotDeleteOwner");
  }
};

export const backfillOwnerInvitationUserId = async ({
  entityId,
  entityType,
  userId,
  memberEmail,
  profileEmail,
  companyEmail,
}: {
  entityId: string;
  entityType: ParticipantType;
  userId: string;
  memberEmail?: string | null;
  profileEmail?: string | null;
  companyEmail?: string | null;
}) => {
  const ownerInvite = pickLinkedOwnerInvitation(
    await prisma.participantInvitation.findMany({
      where: { entityId, entityType, isOwner: true },
      select: { id: true, userId: true, email: true },
      orderBy: { updatedAt: "desc" },
    })
  );
  if (!ownerInvite || ownerInvite.userId === userId) return;
  if (
    !isParticipantOwner({
      memberUserId: userId,
      memberEmail,
      profileEmail,
      ownerInvite,
      companyEmail,
    })
  ) {
    return;
  }
  await prisma.participantInvitation.update({
    where: { id: ownerInvite.id },
    data: { userId },
  });
};

export const getEventMembers = async ({
  exhibitorIds,
  eventId,
  userId,
}: {
  exhibitorIds: string[];
  eventId: string;
  userId: string;
}) => {
  try {
    const members = await prisma.user.findMany({
      where: {
        AND: [
          {
            id: { not: userId },
          },
          {
            OR: [
              { exhibitorIds: { hasSome: exhibitorIds } },
              {
                events: {
                  some: { id: eventId },
                },
              },
            ],
          },
        ],
      },
    });

    return members;
  } catch (error) {
    console.error("Error fetching event members:", error);
    throw new Error("Failed to fetch event members");
  }
};

const COMPANY_ADMIN_EMAIL_TAKEN =
  "Contact email is already used by another account";

type CompanyAdminLookup = {
  teamIds: string[];
  whiteLabelId?: string | null;
  previousEmail?: string | null;
  email?: string | null;
};

const userSelect = { id: true, email: true } as const;

const resolveCompanyAdminUser = async (params: CompanyAdminLookup) => {
  const emails = companyAdminLookupEmails(params.previousEmail, params.email);
  const teamIds = params.teamIds.filter(Boolean);

  const teamUsers =
    teamIds.length && emails.length
      ? await prisma.user.findMany({
          where: { id: { in: teamIds }, email: { in: emails } },
          select: userSelect,
        })
      : teamIds.length && !emails.length
        ? await prisma.user.findMany({
            where: { id: { in: teamIds } },
            select: userSelect,
            take: 1,
          })
        : [];

  return (
    pickPreferredCompanyAdmin(teamUsers, emails) ??
    teamUsers[0] ??
    (emails.length
      ? pickPreferredCompanyAdmin(
          await prisma.user.findMany({
            where: {
              email: { in: emails },
              ...(params.whiteLabelId
                ? { accessibleWhiteLabelId: params.whiteLabelId }
                : {}),
            },
            select: userSelect,
          }),
          emails
        )
      : undefined)
  );
};

const findUserIdByEmail = async (
  email: string,
  whiteLabelId?: string | null
) => {
  if (whiteLabelId) {
    const user = await prisma.user.findUnique({
      where: {
        email_accessibleWhiteLabelId: {
          email,
          accessibleWhiteLabelId: whiteLabelId,
        },
      },
      select: { id: true },
    });
    return user?.id;
  }
  const user = await prisma.user.findFirst({
    where: { email },
    select: { id: true },
  });
  return user?.id;
};

export const getCompanyAdminEmailConflict = async (
  params: CompanyAdminLookup
): Promise<string | null> => {
  const nextEmail = params.email?.trim().toLowerCase() || null;
  const previousEmail = params.previousEmail?.trim().toLowerCase() || null;
  if (!nextEmail || nextEmail === previousEmail) return null;

  const [current, takenUserId] = await Promise.all([
    resolveCompanyAdminUser(params),
    findUserIdByEmail(nextEmail, params.whiteLabelId),
  ]);

  return shouldBlockCompanyAdminEmailChange({
    previousEmail,
    nextEmail,
    currentUserId: current?.id,
    takenUserId,
  })
    ? COMPANY_ADMIN_EMAIL_TAKEN
    : null;
};

export const syncCompanyAdminUserAndProfile = async (params: {
  eventId: string;
  teamIds: string[];
  whiteLabelId?: string | null;
  previousEmail?: string | null;
  email?: string | null;
  name?: string;
  phone?: string | null;
  jobTitle?: string | null;
  bio?: string | null;
  sector?: string | null;
  country?: string | null;
  company?: string | null;
  socialMedia?: {
    linkedin?: string | null;
    facebook?: string | null;
    twitter?: string | null;
    instagram?: string | null;
    website?: string | null;
  };
}) => {
  const user = await resolveCompanyAdminUser(params);

  if (!user) return;

  const nextEmail = params.email?.trim().toLowerCase();
  const takenUserId =
    nextEmail && nextEmail !== user.email?.trim().toLowerCase()
      ? await findUserIdByEmail(nextEmail, params.whiteLabelId)
      : undefined;
  const canWriteEmail = Boolean(
    nextEmail && (!takenUserId || takenUserId === user.id)
  );

  const updateUserData: Prisma.UserUpdateInput = {};
  if (params.name) updateUserData.name = params.name;
  if (params.phone !== undefined) updateUserData.phoneNumber = params.phone;
  if (canWriteEmail) updateUserData.email = nextEmail;
  if (params.bio) updateUserData.about = params.bio;

  if (Object.keys(updateUserData).length > 0) {
    await prisma.user.update({
      where: { id: user.id },
      data: updateUserData,
    });
  }

  const updateProfileData: Prisma.ProfileUpdateManyMutationInput = {};
  if (params.name) updateProfileData.name = params.name;
  if (params.phone !== undefined) updateProfileData.phoneNumber = params.phone;
  if (params.jobTitle !== undefined)
    updateProfileData.jobTitle = params.jobTitle;
  if (params.bio) updateProfileData.bio = params.bio;
  if (canWriteEmail) updateProfileData.email = nextEmail;
  if (params.sector !== undefined) updateProfileData.sector = params.sector;
  if (params.country !== undefined) updateProfileData.country = params.country;
  if (params.company !== undefined) updateProfileData.company = params.company;

  const hasSocialMediaUpdate =
    params.socialMedia &&
    Object.values(params.socialMedia).some((value) => value !== undefined);

  if (Object.keys(updateProfileData).length === 0 && !hasSocialMediaUpdate) {
    return;
  }

  if (!hasSocialMediaUpdate) {
    await prisma.profile.updateMany({
      where: {
        eventId: params.eventId,
        userId: user.id,
      },
      data: updateProfileData,
    });
    return;
  }

  const profiles = await prisma.profile.findMany({
    where: {
      eventId: params.eventId,
      userId: user.id,
    },
    select: { id: true, socialMedia: true },
  });

  await Promise.all(
    profiles.map((profile) => {
      const current = profile.socialMedia;
      const next = params.socialMedia!;
      return prisma.profile.update({
        where: { id: profile.id },
        data: {
          ...updateProfileData,
          socialMedia: {
            linkedin:
              next.linkedin !== undefined
                ? next.linkedin
                : current?.linkedin,
            facebook:
              next.facebook !== undefined
                ? next.facebook
                : current?.facebook,
            twitter:
              next.twitter !== undefined ? next.twitter : current?.twitter,
            instagram:
              next.instagram !== undefined
                ? next.instagram
                : current?.instagram,
            website:
              next.website !== undefined ? next.website : current?.website,
          },
        },
      });
    })
  );
};

